<script lang="tsx">
  import {
    PropType,
    defineComponent,
    computed,
  } from 'vue'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import { BeerStyleObj, ModifierColor } from '@xhs/yam-beer/core'
  import { useEllipsis, useAction, ActionOption } from './useText'

  import initStyle from './style'

  initStyle()

  export default defineComponent({
    name: 'BeerText',
    props: {
      /**
       * 容器 bs style
       * @ignore
       */
      bs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * 渲染的 HTML 标签
       */
      as: {
        type: String as PropType<string>,
        default: 'div',
      },
      /**
       * 样式风格类型
       * @type {h2|h3|body|mute|link|inherit}
       */
      variant: {
        type: String as PropType<string>,
        default: 'body',
      },
      /**
       * 意图颜色，用来呈现文字的意图提示
       * 在 variant  = link / mute 的时候无效
       * @type {primary|success|danger|warn|inherit}
       * @version 4.1.0
       */
      modifier: {
        type: String as PropType<ModifierColor>,
        default: 'inherit',
      },
      /**
       * 是否高亮文字
       * @version 4.1.0
       */
      mark: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否禁用
       * @version 4.1.0
       */
      disabled: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否带文字装饰线
       * @version 4.1.0
       */
      underline: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否带文字删除线
       * @version 4.1.0
       */
      delete: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否加粗
       * @version 4.1.0
       */
      strong: {
        type: Boolean,
        default: false,
      },
      /**
       * 代码文本
       * @version 4.1.0
       */
      code: {
        type: Boolean,
        default: false,
      },
      /**
       * 键盘文本
       * @version 4.1.0
       */
      keyboard: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否斜体
       * @version 4.1.0
       */
      italic: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否超长省略，可以设置 expandeable 属性来允许展开
       * @version 4.1.0
       * @see 示例：可以省略的文本 #可以省略的文本
       */
      ellipsis: {
        type: [Boolean, Object],
        default: false,
      },
      /**
       * 文本的附加操作功能，目前支持 edit 编辑 / copy 复制
       * @version 4.1.0
       * @see 示例：可以复制的文本 #可以复制的文本
       * @see 示例：可以编辑的文本 #可以编辑的文本
       */
      action: {
        type: [String, Object] as PropType<ActionOption>,
        required: false,
      },
    },
    setup(props, { attrs, slots }) {
      const { getContent, renderExpand } = useEllipsis(props.ellipsis)
      const { renderAction, isEdit, renderEdit } = useAction(props.action as ActionOption)
      const bs = computed(() => {
        const textBs: any = {
          'cmp.textType': true,
          cmp: 'textStyle',
        }
        if (props.disabled) {
          textBs.color = 'mute'
          textBs.cursor = 'not-allowed'
          textBs['user-select'] = 'none'
        }
        if (props.variant !== 'link' && props.variant !== 'mute') {
          textBs.color = props.modifier
        }
        return {
          ...textBs,
          ...props.bs,
        }
      })

      function renderWithType(slot: any) {
        if (props.mark) {
          return <mark>{slot}</mark>
        }
        if (props.underline) {
          return <u>{slot}</u>
        }
        if (props.code) {
          return <code class="text-code">{slot}</code>
        }
        if (props.keyboard) {
          return <kbd>{slot}</kbd>
        }
        if (props.delete) {
          return <del>{slot}</del>
        }
        if (props.italic) {
          return <i>{slot}</i>
        }
        if (props.strong) {
          return <strong>{slot}</strong>
        }
        return slot
      }
      return () => {
        const defaultSlot = slots.default?.()

        const cmp = (
          <NewBsBox
            class="typograhpy"
            context={{
              variant: props.variant,
            }}
            {...{
              as: props.as,
              bs: bs.value,
              ...attrs,
            }}
          >
            {isEdit.value ? renderEdit(defaultSlot) : renderWithType(getContent(defaultSlot))}
            {props.ellipsis && renderExpand(defaultSlot)}
            {props.action && renderAction(defaultSlot)}
          </NewBsBox>
        )

        return cmp
      }
    },
  })

</script>
